/**
 * Extracts the domain name from a given URL.
 *
 * This function parses a URL to extract the domain name, removing any protocols (http, https),
 * subdomains, and path or query strings. It handles both standard and two-part TLDs
 * (e.g., co.uk, com.au). If the URL contains subdomains, they are removed, and only the primary
 * domain and its TLD are returned.
 *
 * @param {string} url - The full URL from which to extract the domain name.
 * @returns {string} The extracted domain name.
 *
 * @example
 * // returns "example.com"
 * extractDomainName("https://www.example.com/path/to/resource?query=string");
 */
export function extractDomainName(url: string): string | null {
    try {
        // Remove protocol (http, https), and get the hostname
        // Wrapped in try/catch in case URL constructor encounters an error
        const hostname = new URL(url).hostname;

        // Find & return the domain name
        // This will remove any subdomains and leave the primary domain and TLD
        const domainParts = hostname.split(".").reverse();
        if (domainParts.length > 2) {
            // Check if the TLD is a known two-part TLD (e.g., co.uk, com.au)
            if (domainParts[1].length === 2 && domainParts[0].length === 2) {
                // Return the domain including the two-part TLD
                return (
                    domainParts[2] + "." + domainParts[1] + "." + domainParts[0]
                );
            } else {
                // Regular TLD, return the domain
                return domainParts[1] + "." + domainParts[0];
            }
        }
        return hostname; // Return the hostname directly if it's already a base domain
    } catch (e) {
        // Return null if the above fails (usually because `URL` constructor fails)
        return null;
    }
}
